(() => {
  'use strict';

  const ELEMENT_NAME = 'purty-subscription-bundle';
  if (customElements.get(ELEMENT_NAME)) return;

  const frequencyLabels = {
    'three-monthly': 'Every 3 months',
    'six-monthly': 'Every 6 months',
    'eight-monthly': 'Every 8 months',
  };

  const colourValues = {
    'baby pink': '#f5b6cb',
    black: '#171417',
    'blue pastel': '#b8ddec',
    brown: '#795548',
    cream: '#f4ead9',
    'ice blue': '#c7e8f4',
    nude: '#d7b49e',
    pink: '#ef8fb1',
    'pink pastel': '#f4c7d7',
    purple: '#8d65a8',
    'purple pastel': '#d3bce2',
    red: '#c7354c',
    'soft pink': '#efc3d0',
    tan: '#c6956d',
    white: '#ffffff',
    yellow: '#f0d65d',
  };

  class PurtySubscriptionBundle extends HTMLElement {
    connectedCallback() {
      if (this.initialised) {
        if (this.purchaseModes) this.startPurchaseModes();
        return;
      }
      this.initialised = true;
      this.message = this.querySelector('[data-purty-message]');

      try {
        this.data = JSON.parse(
          this.querySelector('[data-purty-subscription-bundle-data]')?.textContent || '{}'
        );
      } catch (error) {
        this.showMessage('The subscription selector could not be loaded.', 'error');
        return;
      }

      this.packSize = Number(this.dataset.packSize || 3);
      this.currency = this.dataset.currencyCode || 'AUD';
      this.locale = this.dataset.locale || document.documentElement.lang || 'en-AU';
      this.planIds = {
        'three-monthly': String(this.dataset.threeMonthPlanId || '').trim(),
        'six-monthly': String(this.dataset.sixMonthPlanId || '').trim(),
        'eight-monthly': String(this.dataset.eightMonthPlanId || '').trim(),
      };
      this.frequency = this.hasPlan(this.dataset.defaultFrequency)
        ? this.dataset.defaultFrequency
        : Object.keys(frequencyLabels).find((key) => this.hasPlan(key)) || 'three-monthly';
      this.mixOpen = false;
      this.disclosureOpen = false;

      this.sizeIndex = this.findOptionIndex(/size/i, 0);
      this.colourIndex = this.findOptionIndex(/colou?r/i, 1);
      this.selectedVariant =
        this.data.variants?.find(
          (variant) => String(variant.id) === String(this.data.selectedVariantId)
        ) || this.data.variants?.find((variant) => variant.available);

      if (!this.selectedVariant || this.sizeIndex < 0 || this.colourIndex < 0) {
        this.showMessage('This product needs Size and Colour options to use this selector.', 'error');
        return;
      }

      this.master = {
        size: this.selectedVariant.options[this.sizeIndex],
        colour: this.selectedVariant.options[this.colourIndex],
      };
      this.pairs = Array.from({ length: this.packSize }, () => ({ ...this.master }));

      this.cacheElements();
      this.renderMasterOptions();
      this.renderPairRows();
      this.bindEvents();
      this.update();

      if (this.purchaseModes) this.startPurchaseModes();

      document.body.classList.add('purty-custom-subscription-active');
    }

    disconnectedCallback() {
      this.integrationObserver?.disconnect();
      this.drawerObserver?.disconnect();
      this.integrationAbort?.abort();
      window.cancelAnimationFrame(this.integrationFrame);
      this.sticky?.remove();
      this.hiddenOneTime?.forEach((element) => element.removeAttribute('data-purty-one-time-hidden'));
      this.scope?.removeAttribute('data-purty-purchase-scope');
      document.body.classList.remove('purty-subscription-panel-open');
      document.body.classList.remove('purty-custom-subscription-active');
    }

    findOptionIndex(pattern, fallback) {
      const index = (this.data.optionNames || []).findIndex((name) => pattern.test(name));
      if (index >= 0) return index;
      return fallback < (this.data.optionNames || []).length ? fallback : -1;
    }

    cacheElements() {
      this.purchaseModes = this.querySelector('[data-purty-purchase-modes]');
      this.modeInputs = Array.from(this.querySelectorAll('[data-purty-purchase-mode]'));
      this.bundleSaving = this.querySelector('[data-purty-bundle-saving]');
      this.subscribeToggle = this.querySelector('[data-purty-subscribe-toggle]');
      this.subscriptionContent = this.querySelector('[data-purty-subscription-content]');
      this.disclosureIcon = this.querySelector('.purty-subscription-bundle__disclosure-icon');
      this.collapsedSummary = this.querySelector('[data-purty-collapsed-summary]');
      this.offerTotal = this.querySelector('[data-purty-offer-total]');
      this.offerNormal = this.querySelector('[data-purty-offer-normal]');
      this.offerNormalWrap = this.querySelector('[data-purty-offer-normal-wrap]');
      this.offerSaving = this.querySelector('[data-purty-offer-saving]');
      this.offerSavingWrap = this.querySelector('[data-purty-offer-saving-wrap]');
      this.masterControls = this.querySelector('[data-purty-master-controls]');
      this.masterSizeOptions = this.querySelector('[data-purty-master-size-options]');
      this.masterColourOptions = this.querySelector('[data-purty-master-colour-options]');
      this.selectedSizeLabel = this.querySelector('[data-purty-selected-size]');
      this.selectedColourLabel = this.querySelector('[data-purty-selected-colour]');
      this.mixToggle = this.querySelector('[data-purty-mix-toggle]');
      this.mixLabel = this.querySelector('[data-purty-mix-label]');
      this.mixIcon = this.querySelector('.purty-subscription-bundle__mix-icon');
      this.pairList = this.querySelector('[data-purty-pair-list]');
      this.compareTotal = this.querySelector('[data-purty-compare-total]');
      this.total = this.querySelector('[data-purty-total]');
      this.unitPrice = this.querySelector('[data-purty-unit-price]');
      this.renewalCopy = this.querySelector('[data-purty-renewal-copy]');
      this.addButton = this.querySelector('[data-purty-add]');
      this.addLabel = this.querySelector('[data-purty-add-label]');
      this.message = this.querySelector('[data-purty-message]');
      this.frequencyButtons = Array.from(this.querySelectorAll('[data-purty-frequency]'));
    }

    startPurchaseModes() {
      this.integrationAbort?.abort();
      this.integrationObserver?.disconnect();
      this.drawerObserver?.disconnect();
      this.sticky?.remove();
      this.scope = this.closest('[id^="ProductInfo-"], [data-purty-purchase-container]');
      if (!this.scope) {
        // Explain the setup problem; do not guess which other form to hide.
        this.subscriptionContent.hidden = false;
        this.showMessage('Theme setup needed: place this block inside the main product information area.', 'error');
        return;
      }
      this.hiddenOneTime = new Set();
      this.scope.setAttribute('data-purty-purchase-scope', '');
      document.body.classList.add('purty-custom-subscription-active');
      this.integrationAbort = new AbortController();
      const signal = this.integrationAbort.signal;
      this.modeStorageKey = `purty-purchase-mode:${this.data.productId}`;
      let remembered;
      try { remembered = sessionStorage.getItem(this.modeStorageKey); } catch (_) { /* optional */ }
      this.purchaseMode = this.purchaseMode || remembered || this.dataset.defaultPurchaseMode;
      this.purchaseMode = this.purchaseMode === 'subscription' ? 'subscription' : 'one-time';
      this.purchaseModes.hidden = false;
      this.modeInputs.forEach((input) => input.addEventListener('change', () => {
        if (input.checked) this.setPurchaseMode(input.value, true);
      }, { signal }));

      this.integrationObserver = new MutationObserver((mutations) => {
        // App blocks can mount/re-render later. Ignore our own generated text.
        if (mutations.some((mutation) => !this.contains(mutation.target))) {
          this.syncOneTimeVisibility();
          this.updateBundleSaving();
        }
      });
      this.integrationObserver.observe(this.scope, { childList: true, subtree: true, characterData: true });

      this.sticky = document.createElement('div');
      this.sticky.className = 'purty-subscription-sticky';
      this.sticky.hidden = true;
      this.stickyButton = document.createElement('button');
      this.stickyButton.type = 'button';
      this.stickyButton.className = 'purty-subscription-bundle__add';
      this.stickyButton.addEventListener('click', () => this.addSubscription(), { signal });
      this.sticky.append(this.stickyButton);
      document.body.append(this.sticky);
      window.addEventListener('scroll', () => this.syncSticky(), { passive: true, signal });
      window.addEventListener('resize', () => this.syncSticky(), { passive: true, signal });
      window.visualViewport?.addEventListener('resize', () => this.syncSticky(), { signal });
      // Observe drawer state only, not its cart contents. No polling/cart requests.
      this.drawerObserver = new MutationObserver(() => this.syncSticky());
      this.drawerObserver.observe(document.body, { attributes: true, attributeFilter: ['class', 'style'] });
      document.querySelectorAll('cart-drawer, .side-panel, dialog').forEach((drawer) => {
        this.drawerObserver.observe(drawer, { attributes: true, attributeFilter: ['class', 'open', 'aria-hidden', 'style'] });
      });
      document.addEventListener('cart:refresh', () => {
        this.suppressStickyUntil = Date.now() + 1500;
        this.syncSticky();
      }, { signal });
      // Stop a stale native button/form from submitting a one-off while the
      // visitor is in subscription mode. Never rewrite Kaching's form payload.
      this.scope.addEventListener('submit', (event) => {
        if (this.purchaseMode === 'subscription' && this.isOneTimeForm(event.target)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.addButton.focus();
        }
      }, { capture: true, signal });
      this.scope.addEventListener('click', (event) => {
        const button = event.target.closest('button[type="submit"], input[type="submit"]');
        if (this.purchaseMode === 'subscription' && button && this.isOneTimeForm(button.form)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          this.addButton.focus();
        }
      }, { capture: true, signal });
      this.setPurchaseMode(this.purchaseMode);
    }

    isOneTimeForm(form) {
      return form && this.scope?.contains(form) && !this.contains(form) &&
        /\/cart\/add(?:\.js)?(?:\?|$)/.test(form.getAttribute('action') || '');
    }

    setPurchaseMode(mode, userInitiated = false) {
      if (this.loading) return;
      this.purchaseMode = mode === 'subscription' ? 'subscription' : 'one-time';
      this.modeInputs.forEach((input) => { input.checked = input.value === this.purchaseMode; });
      this.subscriptionContent.hidden = this.purchaseMode !== 'subscription';
      this.dataset.purchaseMode = this.purchaseMode;
      document.body.classList.toggle('purty-subscription-panel-open', this.purchaseMode === 'subscription');
      this.syncOneTimeVisibility();
      this.updateBundleSaving();
      this.syncSticky();
      if (userInitiated) {
        try { sessionStorage.setItem(this.modeStorageKey, this.purchaseMode); } catch (_) { /* optional */ }
        this.dispatchEvent(new CustomEvent('purty:purchase-mode-change', {
          bubbles: true, detail: { mode: this.purchaseMode, productId: this.data.productId },
        }));
      }
    }

    syncOneTimeVisibility() {
      const subscription = this.purchaseMode === 'subscription';
      const targets = new Set();
      this.scope.querySelectorAll('kaching-bundle, variant-selects, variant-radios, form[action*="/cart/add"], .shopify-app-block[id*="afterpay"]').forEach((element) => {
        if (this.contains(element)) return;
        if (element.matches('kaching-bundle')) {
          const block = element.closest('.shopify-app-block');
          targets.add(block && !block.contains(this) ? block : element);
        } else {
          targets.add(element);
        }
      });
      this.hiddenOneTime.forEach((element) => {
        if (!subscription || !targets.has(element)) element.removeAttribute('data-purty-one-time-hidden');
      });
      targets.forEach((element) => element.toggleAttribute('data-purty-one-time-hidden', subscription));
      this.hiddenOneTime = targets;
    }

    updateBundleSaving() {
      if (!this.bundleSaving || !this.scope) return;
      const bar = Array.from(this.scope.querySelectorAll('.kaching-bundles__bar'))
        .find((element) => element.dataset.dealBarId === this.dataset.threePackBarId);
      // Use the actual displayed three-pack total, not its advertising/title text.
      const text = bar?.querySelector('.kaching-bundles__bar-price')?.textContent || '';
      const parts = new Intl.NumberFormat(this.locale).formatToParts(12345.6);
      const decimal = parts.find((part) => part.type === 'decimal')?.value || '.';
      const group = parts.find((part) => part.type === 'group')?.value;
      let number = text.trim().replace(/[^0-9.,\s\u00a0\u202f]/g, '').trim();
      // Refuse ambiguous punctuation rather than showing an inflated saving
      // when a currency app formats independently of the document locale.
      const escapeRegex = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const numericPattern = group
        ? `^(?:\\d+|\\d{1,3}(?:${escapeRegex(group)}\\d{3})+)(?:${escapeRegex(decimal)}\\d{1,2})?$`
        : `^\\d+(?:${escapeRegex(decimal)}\\d{1,2})?$`;
      if (!new RegExp(numericPattern).test(number)) number = '';
      if (group) number = number.split(group).join('');
      number = number.replace(/[^0-9.,]/g, '').replace(decimal, '.');
      const cents = /^\d+(?:\.\d{1,2})?$/.test(number) ? Math.round(Number(number) * 100) : NaN;
      const selection = this.validatedSelection();
      const total = selection.valid ? selection.allocations.reduce((sum, plan) => sum + Number(plan.price), 0) : NaN;
      const saving = cents - total;
      this.bundleSaving.hidden = !(saving > 0);
      this.bundleSaving.textContent = saving > 0
        ? `Save ${this.formatMoney(saving)} vs a one-time ${this.packSize}-pack` : '';
    }

    syncSticky() {
      if (!this.sticky) return;
      const blockingPanel = Array.from(document.querySelectorAll('.side-panel.active, cart-drawer.active, dialog[open], [aria-modal="true"]'))
        .some((panel) => panel.getClientRects().length && getComputedStyle(panel).visibility !== 'hidden');
      const keyboard = window.visualViewport && window.visualViewport.height < window.innerHeight * 0.75;
      const show = this.purchaseMode === 'subscription' && window.innerWidth < 750 &&
        this.addButton.getBoundingClientRect().bottom < 0 && !blockingPanel && !keyboard &&
        !(Date.now() < this.suppressStickyUntil);
      this.sticky.hidden = !show;
      this.stickyButton.textContent = this.loading ? 'Adding subscription…' : this.addLabel.textContent;
      this.stickyButton.disabled = this.addButton.disabled;
      this.stickyButton.setAttribute('aria-busy', String(Boolean(this.loading)));
    }

    optionValues(index) {
      const values = [];
      (this.data.variants || []).forEach((variant) => {
        const value = variant.options[index];
        if (value != null && !values.includes(value)) values.push(value);
      });
      return values;
    }

    shortSize(value) {
      return String(value || '').split('=')[0].trim();
    }

    colourFor(value) {
      const key = String(value || '').trim().toLowerCase();
      if (colourValues[key]) return colourValues[key];
      const partial = Object.keys(colourValues).find((name) => key.includes(name));
      return partial ? colourValues[partial] : '#eee5e8';
    }

    optionHasStock(index, value) {
      return (this.data.variants || []).some(
        (variant) => variant.available && variant.options[index] === value
      );
    }

    renderMasterOptions() {
      this.renderPills(
        this.masterSizeOptions,
        this.optionValues(this.sizeIndex),
        'size',
        this.master.size
      );
      this.renderPills(
        this.masterColourOptions,
        this.optionValues(this.colourIndex),
        'colour',
        this.master.colour
      );
      this.updateMasterLabels();
    }

    renderPills(container, values, type, selectedValue) {
      if (!container) return;
      container.replaceChildren();

      values.forEach((value) => {
        const optionIndex = type === 'size' ? this.sizeIndex : this.colourIndex;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = `purty-subscription-bundle__pill purty-subscription-bundle__pill--${type}`;
        button.dataset.purtyOptionType = type;
        button.dataset.purtyOptionValue = value;
        button.setAttribute('aria-label', value);
        button.title = value;
        button.setAttribute('aria-pressed', String(value === selectedValue));
        button.classList.toggle('is-selected', value === selectedValue);
        button.disabled = !this.optionHasStock(optionIndex, value);

        if (type === 'colour') {
          const swatch = document.createElement('span');
          swatch.className = 'purty-subscription-bundle__swatch';
          swatch.style.backgroundColor = this.colourFor(value);
          swatch.setAttribute('aria-hidden', 'true');
          button.append(swatch);
        }

        const text = document.createElement('span');
        text.textContent = type === 'size' ? this.shortSize(value) : value;
        button.append(text);
        container.append(button);
      });
    }

    renderPairRows() {
      if (!this.pairList) return;
      this.pairList.replaceChildren();

      this.pairs.forEach((pair, index) => {
        const row = document.createElement('div');
        row.className = 'purty-subscription-bundle__pair';

        const heading = document.createElement('strong');
        heading.className = 'purty-subscription-bundle__pair-number';
        heading.textContent = `Pair ${index + 1}`;
        row.append(heading);

        row.append(
          this.buildPairSelect(index, 'size', pair.size),
          this.buildPairSelect(index, 'colour', pair.colour)
        );
        this.pairList.append(row);
      });
    }

    buildPairSelect(pairIndex, type, selectedValue) {
      const wrapper = document.createElement('label');
      wrapper.className = 'purty-subscription-bundle__pair-control';
      wrapper.append(document.createTextNode(type === 'size' ? 'Size' : 'Colour'));

      const select = document.createElement('select');
      select.dataset.purtyPairIndex = String(pairIndex);
      select.dataset.purtyPairOption = type;
      select.setAttribute('aria-label', `Pair ${pairIndex + 1} ${type}`);

      const optionIndex = type === 'size' ? this.sizeIndex : this.colourIndex;
      this.optionValues(optionIndex).forEach((value) => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = value;
        option.selected = value === selectedValue;
        option.disabled = !this.optionHasStock(optionIndex, value);
        select.append(option);
      });

      wrapper.append(select);
      return wrapper;
    }

    bindEvents() {
      this.addEventListener('click', (event) => {
        if (this.loading) return;
        const optionButton = event.target.closest('[data-purty-option-type]');
        if (optionButton && !optionButton.disabled) {
          this.setMasterOption(
            optionButton.dataset.purtyOptionType,
            optionButton.dataset.purtyOptionValue
          );
          return;
        }

        const frequencyButton = event.target.closest('[data-purty-frequency]');
        if (frequencyButton && !frequencyButton.disabled) {
          this.frequency = frequencyButton.dataset.purtyFrequency;
          this.update();
        }
      });

      this.subscribeToggle?.addEventListener('click', () => this.toggleDisclosure());
      this.mixToggle?.addEventListener('click', () => this.toggleMix());
      this.pairList?.addEventListener('change', (event) => this.changePairOption(event));
      this.addButton?.addEventListener('click', () => this.addSubscription());
    }

    toggleDisclosure() {
      this.disclosureOpen = !this.disclosureOpen;
      this.subscriptionContent.hidden = !this.disclosureOpen;
      this.subscribeToggle.setAttribute('aria-expanded', String(this.disclosureOpen));
      this.disclosureIcon.textContent = this.disclosureOpen ? '−' : '+';
      document.body.classList.toggle('purty-subscription-panel-open', this.disclosureOpen);
    }

    setMasterOption(type, value) {
      this.master[type] = value;
      this.ensureValidCombination(this.master, type);
      this.pairs = Array.from({ length: this.packSize }, () => ({ ...this.master }));
      this.renderMasterOptions();
      this.renderPairRows();
      this.update();
    }

    changePairOption(event) {
      const select = event.target.closest('[data-purty-pair-index]');
      if (!select) return;

      const index = Number(select.dataset.purtyPairIndex);
      const type = select.dataset.purtyPairOption;
      this.pairs[index][type] = select.value;
      this.ensureValidCombination(this.pairs[index], type);
      this.renderPairRows();
      this.update();
    }

    ensureValidCombination(selection, lastChangedType) {
      if (this.variantFor(selection)?.available) return;

      const otherType = lastChangedType === 'size' ? 'colour' : 'size';
      const otherIndex = otherType === 'size' ? this.sizeIndex : this.colourIndex;
      const replacement = this.optionValues(otherIndex).find((value) => {
        const candidate = { ...selection, [otherType]: value };
        return this.variantFor(candidate)?.available;
      });

      if (replacement) selection[otherType] = replacement;
    }

    toggleMix() {
      this.mixOpen = !this.mixOpen;

      if (!this.mixOpen) {
        this.master = { ...this.pairs[0] };
        this.pairs = Array.from({ length: this.packSize }, () => ({ ...this.master }));
        this.renderMasterOptions();
        this.renderPairRows();
      }

      this.masterControls.hidden = this.mixOpen;
      this.pairList.hidden = !this.mixOpen;
      this.mixToggle.setAttribute('aria-expanded', String(this.mixOpen));
      this.mixLabel.textContent = this.mixOpen
        ? `Use one size & colour for all ${this.packSize}`
        : 'Mix sizes & colours';
      this.mixIcon.textContent = this.mixOpen ? '−' : '+';
      this.update();
    }

    updateMasterLabels() {
      if (this.selectedSizeLabel) this.selectedSizeLabel.textContent = `: ${this.master.size}`;
      if (this.selectedColourLabel) {
        this.selectedColourLabel.textContent = `: ${this.master.colour}`;
      }
    }

    variantFor(selection) {
      const baseOptions = [...this.selectedVariant.options];
      baseOptions[this.sizeIndex] = selection.size;
      baseOptions[this.colourIndex] = selection.colour;

      return (this.data.variants || []).find((variant) =>
        variant.options.every((value, index) => value === baseOptions[index])
      );
    }

    selectedPlanId() {
      return this.planIds[this.frequency];
    }

    hasPlan(frequency) {
      const planId = this.planIds[frequency];
      if (!/^[1-9]\d*$/.test(planId || '')) return false;
      return (this.data.variants || []).some((variant) =>
        variant.sellingPlans?.some((plan) => String(plan.id) === planId)
      );
    }

    frequencyLabel() {
      return frequencyLabels[this.frequency] || '';
    }

    allocationFor(variant) {
      const planId = this.selectedPlanId();
      return variant?.sellingPlans?.find((plan) => String(plan.id) === String(planId));
    }

    selections() {
      return this.mixOpen
        ? this.pairs.map((pair) => ({ ...pair }))
        : Array.from({ length: this.packSize }, () => ({ ...this.master }));
    }

    validatedSelection() {
      if (!this.hasPlan(this.frequency)) {
        return {
          valid: false,
          error: 'This delivery frequency is not available yet. Please choose another option.',
        };
      }

      const selections = this.selections();
      const variants = selections.map((selection) => this.variantFor(selection));
      const unavailableIndex = variants.findIndex((variant) => !variant?.available);

      if (unavailableIndex >= 0) {
        return {
          valid: false,
          error: `Pair ${unavailableIndex + 1} is unavailable in that size and colour.`,
        };
      }

      const allocations = variants.map((variant) => this.allocationFor(variant));
      const missingPlanIndex = allocations.findIndex((allocation) => !allocation);

      if (missingPlanIndex >= 0) {
        return {
          valid: false,
          error: `The selected delivery plan is not available for pair ${missingPlanIndex + 1}.`,
        };
      }

      // This UI promises a fixed, pay-as-you-go price on every delivery.
      // Fail closed if a plan is changed to prepaid or a different later price.
      if (allocations.some((allocation) =>
        !Number.isInteger(allocation.price) || allocation.price < 0 ||
        (allocation.perDeliveryPrice != null && Number(allocation.perDeliveryPrice) !== Number(allocation.price)) ||
        (allocation.priceAdjustments || []).some((price) => Number(price) !== Number(allocation.price))
      )) {
        return { valid: false, error: 'This plan’s recurring price needs checking. Please choose another delivery option or a one-time purchase.' };
      }

      return { valid: true, selections, variants, allocations };
    }

    update() {
      this.frequencyButtons.forEach((button) => {
        const available = this.hasPlan(button.dataset.purtyFrequency);
        const selected = button.dataset.purtyFrequency === this.frequency;
        button.disabled = !available;
        button.classList.toggle('is-selected', selected && available);
        button.setAttribute('aria-pressed', String(selected && available));
        const note = button.querySelector('[data-purty-frequency-note]');
        if (note) {
          note.textContent = available ? '' : 'Unavailable';
          note.hidden = available;
        }
      });

      const selection = this.validatedSelection();
      if (!selection.valid) {
        this.addButton.disabled = true;
        if (this.compareTotal) {
          this.compareTotal.textContent = '';
          this.compareTotal.hidden = true;
        }
        this.total.textContent = '';
        if (this.offerTotal) this.offerTotal.textContent = '';
        if (this.offerNormal) this.offerNormal.textContent = '';
        if (this.offerNormalWrap) this.offerNormalWrap.hidden = true;
        if (this.offerSaving) this.offerSaving.textContent = '';
        if (this.offerSavingWrap) this.offerSavingWrap.hidden = true;
        this.unitPrice.textContent = '';
        this.renewalCopy.textContent = '';
        this.addLabel.textContent = 'Subscription unavailable';
        if (this.collapsedSummary) {
          this.collapsedSummary.textContent = 'Choose an available subscription option';
        }
        this.showMessage(selection.error, 'error');
        this.updateBundleSaving();
        this.syncSticky();
        return;
      }

      const total = selection.allocations.reduce((sum, allocation) => sum + allocation.price, 0);
      const compareTotal = selection.allocations.reduce((sum, allocation, index) => {
        const ordinaryVariantPrice = Number(selection.variants[index]?.price || 0);
        return sum + Math.max(
          Number(allocation.compareAtPrice || 0),
          ordinaryVariantPrice,
          Number(allocation.price || 0)
        );
      }, 0);
      const saving = Math.max(compareTotal - total, 0);
      const unitPrice = Math.round(total / this.packSize);
      const frequencyLabel = this.frequencyLabel().toLowerCase();

      if (this.compareTotal) {
        this.compareTotal.textContent = compareTotal > total ? this.formatMoney(compareTotal) : '';
        this.compareTotal.hidden = compareTotal <= total;
      }
      this.total.textContent = this.formatMoney(total);
      if (this.offerTotal) this.offerTotal.textContent = this.formatMoney(total);
      if (this.offerNormal) this.offerNormal.textContent = this.formatMoney(compareTotal);
      if (this.offerNormalWrap) this.offerNormalWrap.hidden = saving <= 0;
      if (this.offerSaving) this.offerSaving.textContent = this.formatMoney(saving);
      if (this.offerSavingWrap) this.offerSavingWrap.hidden = saving <= 0;
      this.unitPrice.textContent = `${this.formatMoney(unitPrice)} per pair`;
      this.renewalCopy.textContent = `${this.formatMoney(total)} today, then ${this.formatMoney(
        total
      )} ${frequencyLabel} until cancelled.`;
      this.addLabel.textContent = `Add subscription – ${this.formatMoney(
        total
      )}`;
      if (this.collapsedSummary) {
        this.collapsedSummary.textContent = `${this.packSize} pairs • ${this.formatMoney(
          total
        )} per delivery • ${frequencyLabel}`;
      }
      this.addButton.disabled = false;
      this.clearMessage();
      this.updateBundleSaving();
      this.syncSticky();
    }

    formatMoney(cents) {
      const amount = Number(cents || 0) / 100;
      const explicitDollarSymbols = {
        AUD: 'A$',
        CAD: 'C$',
        NZD: 'NZ$',
        USD: 'US$',
      };

      try {
        if (explicitDollarSymbols[this.currency]) {
          const number = new Intl.NumberFormat(this.locale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(amount);
          return `${explicitDollarSymbols[this.currency]}${number}`;
        }

        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(amount);
      } catch (error) {
        return `${this.currency} ${amount.toFixed(2)}`;
      }
    }

    setLoading(loading) {
      this.loading = loading;
      if (loading) {
        this.disabledBeforeLoading = new Map();
        this.querySelectorAll('button, input, select').forEach((control) => {
          this.disabledBeforeLoading.set(control, control.disabled);
          control.disabled = true;
        });
      } else {
        this.disabledBeforeLoading?.forEach((disabled, control) => { control.disabled = disabled; });
      }
      this.addButton.disabled = loading;
      this.addButton.setAttribute('aria-busy', String(loading));
      this.toggleAttribute('data-loading', loading);
      this.syncSticky();
    }

    showMessage(text, type = 'status') {
      if (!this.message) return;
      this.message.replaceChildren();
      if (type === 'success') {
        const icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        icon.setAttribute('class', 'purty-subscription-bundle__message-icon');
        icon.setAttribute('viewBox', '0 0 24 24');
        icon.setAttribute('aria-hidden', 'true');
        icon.setAttribute('focusable', 'false');
        const tick = document.createElementNS('http://www.w3.org/2000/svg', 'path');
        tick.setAttribute('d', 'M5 12.5 9.5 17 19 7');
        tick.setAttribute('fill', 'none');
        tick.setAttribute('stroke', 'currentColor');
        tick.setAttribute('stroke-width', '2.5');
        tick.setAttribute('stroke-linecap', 'round');
        tick.setAttribute('stroke-linejoin', 'round');
        icon.append(tick);
        this.message.append(icon);
      }
      const label = document.createElement('span');
      label.className = 'purty-subscription-bundle__message-text';
      label.textContent = text;
      this.message.append(label);
      this.message.dataset.type = type;
      this.message.hidden = false;
    }

    clearMessage() {
      if (!this.message) return;
      this.message.textContent = '';
      this.message.hidden = true;
      delete this.message.dataset.type;
    }

    async addSubscription() {
      if (this.purchaseModes && this.purchaseMode !== 'subscription') return;
      if (this.addButton.getAttribute('aria-busy') === 'true') return;

      const selection = this.validatedSelection();
      if (!selection.valid) {
        this.showMessage(selection.error, 'error');
        return;
      }

      this.clearMessage();
      this.setLoading(true);

      const bundleToken = `purty-${this.packSize}-${Date.now().toString(36)}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;
      const planId = Number(this.selectedPlanId());
      const frequencyLabel = this.frequencyLabel();
      const quantities = new Map();

      selection.variants.forEach((variant) => {
        quantities.set(String(variant.id), (quantities.get(String(variant.id)) || 0) + 1);
      });

      const items = Array.from(quantities.entries()).map(([variantId, quantity]) => ({
        id: Number(variantId),
        quantity,
        selling_plan: planId,
        properties: {
          _purty_subscription_bundle: `snatched-vag-2-0-${this.packSize}-pack`,
          _purty_bundle_token: bundleToken,
          _purty_bundle_size: String(this.packSize),
          _purty_delivery_frequency: frequencyLabel,
        },
      }));

      try {
        const shopRoot = window.Shopify?.routes?.root || '/';
        // This selector supplies its own selling plans, independently of either app widget.
        // Appstle's current URL guard requires appstle=true to be the FIRST parameter.
        // Keep Kaching's request opt-out too; do not disable either app globally.
        const response = await fetch(`${shopRoot}cart/add.js?appstle=true&kaching_bundles=true`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items,
            sections: ['cart-drawer', 'cart-bubble'],
            sections_url: window.location.pathname,
          }),
        });
        const result = await response.json();

        if (!response.ok) {
          throw new Error(
            result.description || result.message || 'The subscription could not be added.'
          );
        }

        this.showMessage(`${this.packSize}-pair subscription added to cart`, 'success');
        this.refreshCart(result);
      } catch (error) {
        this.showMessage(error.message || 'Something went wrong. Please try again.', 'error');
      } finally {
        this.setLoading(false);
      }
    }

    refreshCart(result) {
      document.dispatchEvent(
        new CustomEvent('cart:refresh', {
          bubbles: true,
          detail: { openCartDrawer: true, cart: result, sections: result.sections },
        })
      );

      document.dispatchEvent(
        new CustomEvent('purty:subscription-bundle-added', {
          bubbles: true,
          detail: { packSize: this.packSize, sellingPlanId: this.selectedPlanId() },
        })
      );

      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.renderContents === 'function') {
        try {
          drawer.renderContents(result);
          return;
        } catch (error) {
          // The theme's cart:refresh listener remains the primary fallback.
        }
      }

      window.setTimeout(() => {
        const currentDrawer = document.querySelector('cart-drawer, .cart-drawer');
        if (currentDrawer && typeof currentDrawer.open === 'function') currentDrawer.open();
      }, 150);
    }
  }

  customElements.define(ELEMENT_NAME, PurtySubscriptionBundle);

  function initialiseSubscriptionCartGuard() {
    if (window.PurtySubscriptionCartGuard) return;

    const state = {
      checking: false,
      correcting: false,
      timer: null,
      preferredLine: null,
      lastCart: null,
    };
    const shopRoot = window.Shopify?.routes?.root || '/';

    function schedule(delay = 450) {
      window.clearTimeout(state.timer);
      state.timer = window.setTimeout(enforcePackIntegrity, delay);
    }

    function packDetails(item) {
      const properties = item?.properties || {};
      const token = properties._purty_bundle_token;
      const expected = Number(properties._purty_bundle_size || 0);
      const isSubscriptionPack = Boolean(properties._purty_subscription_bundle);

      if (!isSubscriptionPack || !token || !Number.isInteger(expected) || expected < 1) {
        return null;
      }

      return { token: String(token), expected };
    }

    function groupedPacks(cart) {
      const groups = new Map();

      (cart.items || []).forEach((item, index) => {
        const details = packDetails(item);
        if (!details) return;

        if (!groups.has(details.token)) {
          groups.set(details.token, {
            token: details.token,
            expected: details.expected,
            items: [],
          });
        }

        groups.get(details.token).items.push({ ...item, cartIndex: index });
      });

      return groups;
    }

    function reduceItem(updates, item, amount) {
      if (!item || amount <= 0) return 0;
      const reduction = Math.min(item.quantity, amount);
      const existingTarget = Object.prototype.hasOwnProperty.call(updates, item.key)
        ? updates[item.key]
        : item.quantity;
      updates[item.key] = Math.max(0, existingTarget - reduction);
      return reduction;
    }

    function correctionFor(cart) {
      const updates = {};
      let capped = false;
      let removedIncomplete = false;
      const preferredItem = Number.isInteger(state.preferredLine)
        ? cart.items[state.preferredLine - 1]
        : null;

      groupedPacks(cart).forEach((group) => {
        const total = group.items.reduce((sum, item) => sum + item.quantity, 0);

        if (total > group.expected) {
          capped = true;
          let excess = total - group.expected;
          const preferredInGroup = group.items.find((item) => item.key === preferredItem?.key);

          if (preferredInGroup) {
            excess -= reduceItem(updates, preferredInGroup, excess);
          }

          const remainingItems = [...group.items]
            .filter((item) => item.key !== preferredInGroup?.key)
            .sort((a, b) => b.quantity - a.quantity);

          remainingItems.forEach((item) => {
            if (excess > 0) excess -= reduceItem(updates, item, excess);
          });
        } else if (total > 0 && total < group.expected) {
          removedIncomplete = true;
          group.items.forEach((item) => {
            updates[item.key] = 0;
          });
        }
      });

      return { updates, capped, removedIncomplete };
    }

    function lockPackQuantityControls(cart) {
      const cartSurfaces = Array.from(
        new Set(
          document.querySelectorAll(
            'cart-drawer, .cart-drawer, #CartDrawer, form[action*="/cart"]'
          )
        )
      );

      cartSurfaces.forEach((surface) => {
        const inputs = Array.from(
          new Set(
            surface.querySelectorAll(
              'input[name="updates[]"], input.quantity__input, input[data-quantity-variant-id]'
            )
          )
        );

        inputs.forEach((input, index) => {
          const line = lineFromControl(input) || index + 1;
          const item = cart.items?.[line - 1];
          if (!packDetails(item)) return;

          input.max = String(item.quantity);
          input.dataset.purtyPackQuantityLocked = '';

          const quantityWrapper =
            input.closest('quantity-input, .quantity, .cart-item__quantity') || input.parentElement;
          const plusButton = quantityWrapper?.querySelector(
            'button[name="plus"], [data-action="increase"], [aria-label*="increase" i], [aria-label*="plus" i]'
          );

          if (plusButton) {
            plusButton.disabled = true;
            plusButton.setAttribute('aria-disabled', 'true');
            plusButton.setAttribute('title', 'Subscription packs are fixed at 3 pairs');
            plusButton.dataset.purtyPackPlusLocked = '';
          }
        });
      });
    }

    async function enforcePackIntegrity() {
      if (state.checking || state.correcting) return;
      state.checking = true;

      try {
        const cartResponse = await fetch(`${shopRoot}cart.js`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (!cartResponse.ok) return;

        const cart = await cartResponse.json();
        state.lastCart = cart;
        const correction = correctionFor(cart);
        const updateKeys = Object.keys(correction.updates);

        if (!updateKeys.length) {
          lockPackQuantityControls(cart);
          return;
        }

        state.correcting = true;
        const response = await fetch(`${shopRoot}cart/update.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            updates: correction.updates,
            sections: ['cart-drawer', 'cart-bubble'],
            sections_url: window.location.pathname,
          }),
        });
        const result = await response.json();
        if (!response.ok) return;

        state.lastCart = result;
        lockPackQuantityControls(result);
        document.dispatchEvent(
          new CustomEvent('cart:refresh', {
            bubbles: true,
            detail: { openCartDrawer: true, cart: result, sections: result.sections },
          })
        );

        if (correction.removedIncomplete) {
          showCartGuardMessage(
            'Subscription packs must contain exactly 3 pairs, so the incomplete pack was removed.'
          );
        } else if (correction.capped) {
          showCartGuardMessage('Subscription packs are limited to exactly 3 pairs.');
        }
      } catch (error) {
        console.warn('Purty subscription cart validation could not complete.', error);
      } finally {
        state.preferredLine = null;
        state.checking = false;
        state.correcting = false;
      }
    }

    function lineFromControl(control) {
      const container = control.closest(
        '[data-cart-item-key], [data-key], [data-line], [data-index], .cart-item, [id^="CartDrawer-Item-"], [id^="CartItem-"]'
      );
      const directLine = Number(
        control.dataset.line ||
          control.dataset.index ||
          container?.dataset.line ||
          container?.dataset.index
      );
      if (Number.isInteger(directLine) && directLine > 0) return directLine;

      const id = `${control.id || ''} ${container?.id || ''}`;
      const match = id.match(/(?:quantity|item)[-_]?(\d+)/i);
      return match ? Number(match[1]) : null;
    }

    function isCartQuantityControl(target) {
      if (!(target instanceof Element)) return false;
      const control = target.closest('button, a, input, select');
      if (!control) return false;
      if (!control.closest('cart-drawer, .cart-drawer, #CartDrawer, form[action*="/cart"]')) {
        return false;
      }

      const signature = [
        control.id,
        control.name,
        control.className,
        control.getAttribute('aria-label'),
        control.getAttribute('href'),
      ]
        .filter(Boolean)
        .join(' ');

      if (!/quantity|updates|plus|minus|remove|delete/i.test(signature)) return false;
      state.preferredLine = lineFromControl(control);
      return true;
    }

    function showCartGuardMessage(message) {
      let notice = document.querySelector('[data-purty-cart-guard-message]');
      if (!notice) {
        notice = document.createElement('div');
        notice.className = 'purty-subscription-cart-notice';
        notice.dataset.purtyCartGuardMessage = '';
        notice.setAttribute('role', 'status');
        notice.setAttribute('aria-live', 'polite');
        document.body.append(notice);
      }

      notice.textContent = message;
      notice.classList.add('is-visible');
      window.clearTimeout(notice.hideTimer);
      notice.hideTimer = window.setTimeout(() => notice.classList.remove('is-visible'), 4200);
    }

    function start() {
      schedule(0);

      document.addEventListener(
        'click',
        (event) => {
          if (isCartQuantityControl(event.target)) schedule(700);
        },
        true
      );
      document.addEventListener(
        'change',
        (event) => {
          if (isCartQuantityControl(event.target)) schedule(500);
        },
        true
      );
      ['cart:refresh', 'cart:updated', 'cart:change', 'purty:subscription-bundle-added'].forEach(
        (eventName) => document.addEventListener(eventName, () => schedule(300))
      );

      const observer = new MutationObserver((mutations) => {
        const cartChanged = mutations.some((mutation) => {
          const target = mutation.target instanceof Element ? mutation.target : mutation.target.parentElement;
          return target?.closest?.('cart-drawer, .cart-drawer, #CartDrawer, form[action*="/cart"]');
        });
        if (cartChanged) schedule(350);
      });
      observer.observe(document.body, { childList: true, subtree: true });
    }

    window.PurtySubscriptionCartGuard = {
      check: () => schedule(0),
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', start, { once: true });
    } else {
      start();
    }
  }

  initialiseSubscriptionCartGuard();
})();
